/**
 * Full-page geo-block gate.
 *
 * Pings the backend on mount using a cheap, always-existing endpoint.
 * If the backend responds with HTTP 451 (Unavailable For Legal Reasons)
 * — the code emitted by the server-side GeoBlockMiddleware for
 * blocked countries or VPN/proxy IPs — this component renders a
 * full-screen "Website Not Available" message and prevents the rest of
 * the React app from mounting.
 *
 * Also installs a global axios response interceptor so that ANY later
 * API call returning 451 (e.g. a browser fingerprint or IP that
 * changes mid-session) also flips the app into the blocked state.
 *
 * Fail-open: if the ping fails for network reasons other than 451 we
 * ALLOW the app through — a flaky ISP shouldn't nuke the whole site.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Shield } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function BlockScreen({ reason, countryCode }) {
  const title = reason === 'vpn'
    ? 'Website not visible on VPN'
    : 'Website not available in your region';
  const subtitle = reason === 'vpn'
    ? 'We\u2019ve detected that you\u2019re connecting through a VPN, proxy, or datacentre network. Please disconnect from your VPN and refresh the page to continue.'
    : 'Access to this website is currently restricted for visitors in your region.';

  return (
    <div
      className="min-h-screen w-full flex flex-col items-center justify-center bg-neutral-950 text-neutral-100 px-6"
      data-testid="geoblock-screen"
      style={{
        // Kill any accidental horizontal overflow from residual page content
        // while this overlay is visible.
        position: 'fixed', inset: 0, zIndex: 9999,
      }}
    >
      <div className="max-w-lg text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-lime-600/15 mb-6">
          <Shield size={32} weight="duotone" className="text-lime-400" />
        </div>
        <h1 className="font-outfit font-bold text-3xl sm:text-4xl tracking-tight mb-4">{title}</h1>
        <p className="text-neutral-400 leading-relaxed">{subtitle}</p>
        {countryCode && (
          <p className="text-neutral-600 text-xs mt-6" data-testid="geoblock-country">
            Detected origin: <span className="uppercase font-mono">{countryCode}</span>
          </p>
        )}
        <div className="mt-10 flex justify-center">
          <button
            onClick={() => window.location.reload()}
            className="px-5 py-2.5 rounded-full bg-lime-600 hover:bg-lime-500 text-neutral-950 font-medium text-sm transition-colors"
            data-testid="geoblock-retry"
          >
            Try again
          </button>
        </div>
      </div>
      <p className="absolute bottom-6 text-[11px] text-neutral-600 tracking-wider uppercase">
        North Somerset Environment Company Ltd
      </p>
    </div>
  );
}

export function GeoBlockGate({ children }) {
  const [state, setState] = useState({ status: 'checking', reason: null, countryCode: null });

  useEffect(() => {
    // Install a global response interceptor: any 451 that lands in any
    // future API call flips the app into blocked mode too.
    const interceptorId = axios.interceptors.response.use(
      (res) => res,
      (error) => {
        if (error?.response?.status === 451) {
          const detail = error.response.data || {};
          setState({
            status: 'blocked',
            reason: detail.reason || (detail.country_code ? 'country' : 'blocked'),
            countryCode: detail.country_code || null,
          });
        }
        return Promise.reject(error);
      }
    );

    // Ping a cheap, always-existing public endpoint. If it 451s we're
    // blocked. If it succeeds we're allowed through. If it fails for any
    // other reason (network, 5xx, timeout) we fail-open — showing a
    // false-positive block screen would be worse than allowing traffic.
    const ping = async () => {
      try {
        await axios.get(`${API}/page-content/hero`, { timeout: 8000 });
        setState({ status: 'allowed', reason: null, countryCode: null });
      } catch (error) {
        if (error?.response?.status === 451) {
          const detail = error.response.data || {};
          setState({
            status: 'blocked',
            reason: detail.reason || (detail.country_code ? 'country' : 'blocked'),
            countryCode: detail.country_code || null,
          });
        } else {
          // Fail-open on network / 5xx / anything else
          setState({ status: 'allowed', reason: null, countryCode: null });
        }
      }
    };
    ping();

    return () => {
      axios.interceptors.response.eject(interceptorId);
    };
  }, []);

  if (state.status === 'blocked') {
    return <BlockScreen reason={state.reason} countryCode={state.countryCode} />;
  }
  // While checking (very brief — one HTTP request), still render the app
  // beneath. If it turns out we're blocked, the screen slaps on top.
  return children;
}
